import { dataFrom } from '@zyno-io/openapi-client-codegen';
import { handleError } from '@zyno-io/vue-foundation';
import { effectScope, shallowReactive, watch } from 'vue';

import { activeTenantIdRef } from '@/global.shared';
import { SalesSalesContactSalesSummariesApi } from '@/openapi-clients-generated/sales';
import { SuiteCrmContactNotesApi, SuiteCrmContactsApi } from '@/openapi-clients-generated/suite';
import { hasPermission } from '@/shared/helpers/permissions.helpers';

import type { IContactAlertPreview, IContactSummaryContact, IContactSummaryData } from './contact-summary.helpers';

import { canLoadContactSummaries, contactSummaryAccessKey } from './contact-summary.access';
import { summarizeAlertNotes } from './contact-summary.helpers';

/**
 * Sales-side summary (lifetime spend, last sale/booking, next booking) per CRM contact, shared by the
 * hover card, the CRM list columns and (later) the incoming-call popover. Same shape as
 * ContactResolution: batched, cached, reactive; consumers read `get(id)` and re-render when the
 * batch lands.
 */

const FLUSH_DELAY_MS = 50;
const BATCH_MAX = 100; // server-enforced
const FRESH_MS = 60 * 1000;
const FAILURE_BACKOFF_MS = 30 * 1000;

export interface IContactSummaryIncluded {
    totalSpend: boolean;
    lastClosedSale: boolean;
    bookings: boolean;
}

export interface IContactSummaryEntry {
    summary: IContactSummaryData;
    asOf: string;
    included: IContactSummaryIncluded;
    fetchedAt: number;
}

export type ContactSummaryStatus = 'idle' | 'loading' | 'loaded' | 'error';

// Shallow: entries are read-only to consumers; map reads/writes still track.
const entries = shallowReactive(new Map<string, IContactSummaryEntry>());
// id → 'loading' (queued or in flight, no entry yet) | 'error'. Reactive so status() re-renders.
const marks = shallowReactive(new Map<string, 'loading' | 'error'>());
const deniedState = shallowReactive({ value: false });
const failedAt = new Map<string, number>();
// Responses whose fetch started before an invalidation are dropped (they predate the change).
const invalidatedAt = new Map<string, number>();
let invalidatedAllAt = -Infinity;
const pending = new Set<string>();
const inFlight = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on reset; responses from a previous generation are dropped.
let generation = 0;

const identities = shallowReactive(new Map<string, IContactSummaryContact>());
const identityPromises = new Map<string, Promise<IContactSummaryContact | undefined>>();

// Alert-note previews (Suite notes, filtered to isAlert). Same freshness window as summaries.
const alerts = shallowReactive(new Map<string, { preview: IContactAlertPreview; fetchedAt: number }>());
const alertPromises = new Map<string, Promise<void>>();

function isFresh(id: string): boolean {
    const entry = entries.get(id);
    if (!entry) return false;
    const now = Date.now();
    if (now - entry.fetchedAt >= FRESH_MS) return false;
    const next = entry.summary.nextBooking;
    return !next || now < new Date(next.start).getTime();
}

function scheduleFlush() {
    // Plain setTimeout, not a lodash debounce: the visual-test harness freezes Date.now().
    if (!flushTimer) flushTimer = setTimeout(flushPending, FLUSH_DELAY_MS);
}

async function flushPending() {
    flushTimer = null;
    const startedGeneration = generation;
    const batch = [...pending].slice(0, BATCH_MAX);
    if (!batch.length) return;
    for (const id of batch) {
        pending.delete(id);
        inFlight.add(id);
    }
    if (pending.size) scheduleFlush();
    const startedAt = Date.now();

    try {
        const data = dataFrom(await SalesSalesContactSalesSummariesApi.postSalesContactSalesSummariesIndex({ body: { contactIds: batch } }));
        if (generation !== startedGeneration) return;
        const now = Date.now();
        const returned = new Set<string>();
        for (const summary of data?.summaries ?? []) {
            if (invalidatedAllAt >= startedAt || (invalidatedAt.get(summary.contactId) ?? -Infinity) >= startedAt) continue;
            returned.add(summary.contactId);
            entries.set(summary.contactId, { summary, asOf: data.asOf, included: data.included, fetchedAt: now });
            marks.delete(summary.contactId);
            failedAt.delete(summary.contactId);
        }
        // Ids the server didn't answer for stay uncached (an existing stale entry keeps serving).
        for (const id of batch) if (!returned.has(id) && !entries.has(id)) marks.delete(id);
    } catch (err) {
        if (generation !== startedGeneration) return;
        if ((err as { response?: { status?: number } } | undefined)?.response?.status === 403) {
            // No Sales permission at all: stop asking until the session/permissions change.
            deniedState.value = true;
            for (const id of batch) marks.set(id, 'error');
            return;
        }
        const now = Date.now();
        for (const id of batch) {
            failedAt.set(id, now);
            if (!entries.has(id)) marks.set(id, 'error');
        }
        handleError(err, 'Failed to load contact summaries');
    } finally {
        if (generation === startedGeneration) {
            for (const id of batch) inFlight.delete(id);
        }
    }
}

// Permissions changed (role edit, tenant switch): cached summaries may now over- or under-share.
// Started lazily on first use, since the access computed reads GlobalState. Detached scope so the
// watch outlives whichever component happened to make the first request.
let watchingAccess = false;
function ensureAccessWatch() {
    if (watchingAccess) return;
    watchingAccess = true;
    effectScope(true).run(() => watch(contactSummaryAccessKey, () => ContactSummaries.reset()));
}

export const ContactSummaries = {
    /** Queue contacts for loading. Skips queued, in-flight, fresh and recently failed ids. */
    request(ids: (string | null | undefined)[], opts: { force?: boolean } = {}) {
        ensureAccessWatch();
        if (!canLoadContactSummaries.value || deniedState.value) return;
        const now = Date.now();
        let queued = false;
        for (const id of ids) {
            if (!id) continue;
            if (pending.has(id) || inFlight.has(id)) continue;
            if (!opts.force) {
                if (isFresh(id)) continue;
                const failed = failedAt.get(id);
                if (failed !== undefined && now - failed < FAILURE_BACKOFF_MS) continue;
            }
            pending.add(id);
            if (!entries.has(id)) marks.set(id, 'loading');
            queued = true;
        }
        if (queued) scheduleFlush();
    },

    /** Cached summary entry (possibly stale), or undefined. */
    get(id: string | null | undefined): IContactSummaryEntry | undefined {
        return id ? entries.get(id) : undefined;
    },

    status(id: string | null | undefined): ContactSummaryStatus {
        if (!id) return 'idle';
        if (entries.has(id)) return 'loaded';
        if (deniedState.value) return 'error';
        return marks.get(id) ?? 'idle';
    },

    /** Drop cached entries (all when no ids) so the next request re-fetches. */
    invalidate(ids?: string[]) {
        const now = Date.now();
        if (ids) for (const id of ids) invalidatedAt.set(id, now);
        else invalidatedAllAt = now;
        const targets = ids ?? [...entries.keys()];
        for (const id of targets) {
            entries.delete(id);
            failedAt.delete(id);
            if (marks.get(id) === 'error') marks.delete(id);
        }
    },

    /** Contact identity for a bare id (Suite), cached; concurrent calls share one request. */
    loadIdentity(id: string): Promise<IContactSummaryContact | undefined> {
        const cached = identities.get(id);
        if (cached) return Promise.resolve(cached);
        if (!hasPermission('crm')) return Promise.resolve(undefined);
        const inflight = identityPromises.get(id);
        if (inflight) return inflight;
        const startedGeneration = generation;
        const promise = (async () => {
            try {
                const c = dataFrom(await SuiteCrmContactsApi.getCrmContactsShow({ path: { id } }));
                const identity: IContactSummaryContact = {
                    id: c.id,
                    avatarId: c.avatarId,
                    displayName: c.displayName,
                    firstName: c.firstName,
                    lastName: c.lastName,
                    company: c.company,
                    hasAlerts: c.hasAlerts,
                    email: (c.emails.find(e => e.id === c.primaryEmailId) ?? c.emails[0])?.email
                };
                if (generation === startedGeneration) identities.set(id, identity);
                return identity;
            } catch (err) {
                if (generation === startedGeneration) handleError(err, 'Failed to load contact');
                return undefined;
            } finally {
                identityPromises.delete(id);
            }
        })();
        identityPromises.set(id, promise);
        return promise;
    },

    identity(id: string | null | undefined): IContactSummaryContact | undefined {
        return id ? identities.get(id) : undefined;
    },

    /** Load the alert-note preview for one contact (needs crm.notesRead). Fresh entries are skipped. */
    loadAlerts(id: string): Promise<void> {
        if (!hasPermission('crm', 'notesRead')) return Promise.resolve();
        const cached = alerts.get(id);
        if (cached && Date.now() - cached.fetchedAt < FRESH_MS) return Promise.resolve();
        const inflight = alertPromises.get(id);
        if (inflight) return inflight;
        const startedGeneration = generation;
        let promise: Promise<void> | undefined;
        promise = (async () => {
            try {
                const notes = dataFrom(await SuiteCrmContactNotesApi.getCrmContactNotesIndex({ path: { contactId: id } }));
                if (generation === startedGeneration) alerts.set(id, { preview: summarizeAlertNotes(notes), fetchedAt: Date.now() });
            } catch (err) {
                if (generation === startedGeneration) handleError(err, 'Failed to load alert notes');
            } finally {
                if (alertPromises.get(id) === promise) alertPromises.delete(id);
            }
        })();
        alertPromises.set(id, promise);
        return promise;
    },

    alerts(id: string | null | undefined): IContactAlertPreview | undefined {
        return id ? alerts.get(id)?.preview : undefined;
    },

    /** A contact's notes changed: drop its alert preview so the next card re-fetches. */
    invalidateAlerts(id: string) {
        alerts.delete(id);
        alertPromises.delete(id);
    },

    /** Clear all state (tenant switch / logout / access change). */
    reset() {
        generation++;
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        pending.clear();
        inFlight.clear();
        failedAt.clear();
        invalidatedAt.clear();
        invalidatedAllAt = -Infinity;
        entries.clear();
        marks.clear();
        identities.clear();
        identityPromises.clear();
        alerts.clear();
        alertPromises.clear();
        deniedState.value = false;
    }
};

// Tenant switch / sign-out: summaries are tenant-scoped. Watched here rather than reset from
// `@/global`, which must not import this module (it would close an import cycle at boot).
watch(activeTenantIdRef, () => ContactSummaries.reset());
